import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export function useJobPosts(orgId?: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["job_posts", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createPost = useMutation({
    mutationFn: async (input: Omit<TablesInsert<"job_posts">, "org_id">) => {
      const { data, error } = await supabase
        .from("job_posts")
        .insert({ ...input, org_id: orgId! })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job_posts", orgId] });
    },
  });

  const updatePost = useMutation({
    mutationFn: async ({ id, ...patch }: TablesUpdate<"job_posts"> & { id: string }) => {
      const { data, error } = await supabase
        .from("job_posts")
        .update(patch)
        .eq("id", id)
        .eq("org_id", orgId!)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job_posts", orgId] });
    },
  });

  return { posts: query.data ?? [], isLoading: query.isLoading, createPost, updatePost };
}

const CRAWLER_META_SELECT = "source_url, source_name, source_type, company_name, company_logo_url, external_apply_url, crawler_score";

export function usePublishedJobs() {
  return useQuery({
    queryKey: ["published_jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select(`*, partner_orgs(name, industry, is_auto_created, source_origin, logo_url), crawler_metadata(${CRAWLER_META_SELECT})`)
        .eq("status", "publicada")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useJobPostDetail(jobId?: string) {
  return useQuery({
    queryKey: ["job_post", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select(`*, partner_orgs(name, industry, is_auto_created, source_origin, logo_url), crawler_metadata(${CRAWLER_META_SELECT})`)
        .eq("id", jobId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}
